"use client"

import Link from "next/link";
import React from "react";
import { Edit, Calendar } from "lucide-react";
import { StaticImageData } from "next/image";

import { HeadingText } from "@/components/atoms/HeadingText/heading-text";
import Text from "@/components/atoms/Text/text";
import { renderSlide } from "../Hero/hero";
import HeroCarousel from "../Carousel/HeroCarousel";

export type HeroImage = { feature: StaticImageData | undefined };

export type BannerHeroProps = {
  translations: {
    heading: string;
    description: string;
    blogButtonText: string;
    eventButtonText: string;
    buttonNote: string;
  };
};

const BlogBanner = ({ translations }: BannerHeroProps) => {

  const creativeProfessionalsSlides = [
    // Bloggers
    'https://images.unsplash.com/photo-1463171379579-3fdfb86d6285?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    'https://images.unsplash.com/photo-1545239351-ef35f43d514b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    'https://images.unsplash.com/photo-1496171367470-9ed9a91ea931?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    'https://images.unsplash.com/photo-1501504905252-473c47e087f8?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    'https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80'
  ];

  const renderSlide = (slide: string, index: number) => (
    <div
      key={index}
      className="relative w-full h-[480px] md:h-[450px] lg:h-[400px]"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${slide})` }}
      />
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-gray-950/80 to-gray-800/80" />
    </div>
  );

  return (
    <section className="relative w-full overflow-hidden">
      <HeroCarousel
        slides={creativeProfessionalsSlides}
        renderSlide={renderSlide}
        contentPosition="static-overlay"
        staticContent={
          <div className="mx-auto max-w-screen-2xl px-4 py-10 md:py-24 sm:px-6">
            <div className="flex flex-col-reverse items-center gap-8 md:flex-row md:justify-between">
              {/* Text Content */}
              <div className="w-full max-w-xl text-center md:text-left">
                <HeadingText
                  level={2}
                  className="mb-4 text-2xl font-bold leading-snug !text-gray-50 sm:text-3xl md:text-4xl"
                >
                  {translations.heading}
                </HeadingText>
                <Text className="mb-6 !text-gray-300 sm:text-base">
                  {translations.description}
                </Text>

                {/* Buttons */}
                <div className="mb-4 flex flex-wrap justify-center gap-4 md:justify-start">
                  <Link href="/dashboard/add-story">
                    <button className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 transition">
                      <Edit size={18} className="mr-2" />
                      {translations.blogButtonText}
                    </button>
                  </Link>

                  <Link href="/dashboard/add-event">
                    <button className="flex items-center rounded-lg bg-black px-4 py-2 text-white hover:bg-black/80 transition hover:shadow-md">
                      <Calendar size={18} className="mr-2" />
                      {translations.eventButtonText}
                    </button>
                  </Link>
                </div>

                <p className="text-sm text-blue-600 dark:text-blue-400">
                  {translations.buttonNote}
                </p>
              </div>
            </div>
          </div>
        }
        autoPlay={true}
        interval={3000}
      />
    </section>
  );
};

export default BlogBanner;
